/**
 * **信用卡账单区** —— 债权债务页的第一段（批次 `94`）
 *
 * 依据：原型 `docs/03-UX与UI设计/15-债权债务_原型.html:248-286`「信用卡账单」卡 ·
 * B 域 PRD §5.2.2（账期与应还）· §5.2.3（余额档位）· `AC-B2.6`（红线）。
 *
 * ## 🔴 为什么这块必须独立成段（而不是塞进"日常账户"列表）
 *
 * 负责人 2026-10-21 的原话是「信用卡在资产里面不太方便查看」。真因很具体：
 * **服务端早就算好了当期账单（`AccountView.credit.bill`），资产页一个字都没渲染** ——
 * 12 张卡在「日常账户」里长成 12 行 `名称 · 归属人 · CNY`，跟储蓄卡没有区别。
 *
 * 而信用卡要回答的是完全不同的问题：**这期该还多少、什么时候还、还能刷多少、下期已经花了多少**。
 * 那是"负债的当期义务"，与"我有多少现金"不是同一件事 ⇒ 与债权债务同页（原型的排法）。
 *
 * ## 🔴 数字全由服务端下发（本组件不重算）
 *
 * 账期窗口是**半开区间 + 月末 clamp**（账单日 31 在 2 月按月末）。端侧重算一次，
 * 就会出现"账单日当天算哪一期"的第二个答案 —— 而那是**与银行账单对不上**的直接来源。
 * ⇒ 本文件只做：取数（`GET /api/accounts/credit-bills`）→ 排版 → 触发还款/看往期。
 *
 * ## ⚠️ 本批不做（诚实登记）
 *
 * - **每张卡的历史消费明细**（点进某期看那一期买了什么）—— 需要按账单窗口反查流水，
 *   属 P1；当前「往期」只给**每期汇总**（消费 / 退款 / 应还）。
 * - **分期 / 最低还款 / 自动还款**：P1。
 */

import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  AccountType,
  CreditHistoryDefaultMonths,
  divideExact,
  formatMoneyText,
  toCents,
  type AccountView,
  type CreditBillsView,
} from '@jiaying/shared';
import { listCreditBills } from '../api/endpoints.js';
import { creditStandingLabel, creditUsageLabel } from '../features/account-labels.js';
import { Amount } from './Amount.js';
import { CreditRepayDialog } from './CreditRepayDialog.js';
import { Grid } from './Grid.js';
import { Loading } from './Loading.js';

/**
 * 往期展示期数 —— 🔴 批 `106-A` 由 3 改为 **6**（`AC-B2.32`）。
 *
 * 依据：规格 §8「最近 **6** 期独立 + 更早合并」。批 `94` 落地时按当时的口头需求
 * 「之前 3 个月」写了 3，与规格不符 ⇒ 本批改回 6（常量取自内核 `CreditHistoryDefaultMonths`，单一源）。
 */
const HISTORY_MONTHS = CreditHistoryDefaultMonths;

export interface CreditCardSectionProps {
  /**
   * 本组要展示的**信用卡账户** —— 🔴 **渲染以它为准**（`DR-058`）。
   *
   * 合并前是以账单接口返回的 `items` 为准渲染的；那样一旦账单接口失败或某张卡
   * 没出现在结果里，这张卡就**整块消失**（合并后更致命：账户行已经不再单独画了）。
   * ⇒ 账户信息永远画得出来，账单只是附加信息。
   */
  accounts: readonly AccountView[];
  /** 全量账户（**仅用于**「从哪个账户还款」的候选）；省略 = 只用 `accounts` */
  allAccounts?: readonly AccountView[];
  /** 🆕 账户操作（合并成一张卡后，记一笔 / 明细 / 编辑 / 删除都在卡上做） */
  onNewTxn?: (account: AccountView) => void;
  onEdit?: (account: AccountView) => void;
  onDelete?: (account: AccountView) => void;
  onOpenLedger?: (account: AccountView) => void;
}

export function CreditCardSection({
  accounts,
  allAccounts,
  onNewTxn,
  onEdit,
  onDelete,
  onOpenLedger,
}: CreditCardSectionProps): ReactElement {
  const tk = useTheme().jiaying.tokens;
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['creditBills', HISTORY_MONTHS],
    queryFn: () => listCreditBills(HISTORY_MONTHS),
  });
  const [repayId, setRepayId] = useState<string | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);

  if (q.isPending) return <Loading py={3} />;
  if (q.isError) {
    return (
      <Alert severity="error" data-testid="credit-bills-error">
        信用卡账单读取失败：{String(q.error)}
      </Alert>
    );
  }

  const items = q.data.items;
  /** 🔴 渲染基准 = 账户（不是账单接口的 items）—— 保证"卡在、账单接口挂了"时卡仍看得见 */
  const creditAccounts = accounts.filter((a) => a.type === AccountType.Credit);
  const payerPool = allAccounts ?? accounts;
  const billOf = (accountId: string): CreditBillsView | null =>
    items.find((i) => i.accountId === accountId) ?? null;
  const repayItem = repayId === null ? null : (items.find((i) => i.accountId === repayId) ?? null);
  const historyItem = historyId === null ? null : (items.find((i) => i.accountId === historyId) ?? null);

  /**
   * 🔴 还款方候选：**同币种的日常账户**（`cash` / `debit` / `ewallet`）。
   *
   * 不收信用卡（自己还自己 = 一次转账两腿都在卡上，净额 0 但账单窗口错乱）、
   * 不收 `contra` / `investment` / `internal`（那些不是"可支付的钱"）。
   */
  const payersFor = (item: CreditBillsView): AccountView[] =>
    payerPool.filter(
      (a) =>
        a.id !== item.accountId &&
        a.currency === item.currency &&
        (a.type === 'cash' || a.type === 'debit' || a.type === 'ewallet'),
    );

  /**
   * 本期应还合计 —— 只累加**有账单数据**的卡。
   * 🔴 单一币种才给合计（`DR-004`：不跨币种相加）；多币种时宁可不显示。
   *
   * ⚠️ 未配账期的卡（`current === null`）在这里只贡献 0（求和必然如此），**合计数因此是下界**。
   * 本批（`105`）**不动**这一行：它没有把 0 冒充成"某张卡的应还"（那张卡的卡面上写的是
   * 「未配置账期」，一个字都不显示金额）—— 但"合计少算了几张卡"这件事仍可能被误读，
   * 是否在合计旁标注"另有 N 张卡未配置账期"**待产品拍板**（见 changelog §不含什么）。
   */
  const bills = creditAccounts
    .map((a) => billOf(a.id))
    .filter((b): b is CreditBillsView => b !== null);
  const currencies = [...new Set(bills.map((b) => b.currency))];
  const totalDue =
    currencies.length === 1 ? bills.reduce((sum, b) => sum + (b.current?.due ?? 0), 0) : null;

  return (
    <Box data-testid="credit-section">
      {/*
        🔴 合并后**不再重复**「信用卡（N）」—— 外层分组头已经有数量与小计。
        这里只给口径说明 + 本期应还合计（两者语义不同：分组小计是余额，这里是账单应还）。
      */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: 2, pt: 1.5, pb: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            每张卡一张卡面：本期应还 · 账期与剩余额度 · 未出账单 · 近 {HISTORY_MONTHS} 期 ·
            还款与账户设置
          </Typography>
        </Box>
        {totalDue !== null && totalDue > 0 && (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              本期应还合计
            </Typography>
            <Amount
              cents={toCents(totalDue)}
              currency={currencies[0] ?? 'CNY'}
              sx={{ fontSize: '1.125rem', fontWeight: 700 }}
            />
          </Box>
        )}
      </Stack>

      {creditAccounts.length === 0 ? (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            还没有信用卡账户。用上方的「新建账户」选「信用卡」，并填上账单日与还款日 ——
            填了账期才能算出本期应还（不填不会瞎猜一个日期）。
          </Typography>
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 2 }}>
          <Grid cols={2} sx={{ alignItems: 'start' }}>
            {creditAccounts.map((acc) => {
              const bill = billOf(acc.id);
              /**
               * 🔴 账单**算不出来**的两种情形都走这条诚实分支（批 `105` · K5）：
               *
               * | 情形 | 含义 |
               * |---|---|
               * | `bill === null` | 这张卡**不在**账单接口结果里（接口降级） |
               * | `bill.current === null` | 卡在结果里，但**没配账期** ⇒ 本期应还根本算不出来 |
               *
               * 第二种原本漏了：完整分支里 `item.current?.due ?? 0` 会把它渲染成
               * 「本期应还 ¥0.00」+ 一个「还款」按钮，用户读成"这期不用还"，而事实是"算不出来"
               * —— **比不显示危险**（`DR-058` D1 原话：「账单数字一概不显示，不拿 0 冒充」）。
               * ⇒ 这里**不许 `?? 0` 兜底**，也不许渲染还款按钮。
               *
               * 🔴 卡面本身照画：合并后账户行已经不再单独渲染，这里若 return null，这张卡就
               * **彻底消失**了 —— 用户会以为卡被删了（比"没有账单数字"严重得多）。
               */
              const billMissing = bill === null || bill.current === null;
              if (billMissing) {
                return (
                  <Card key={acc.id} variant="outlined" data-testid={`credit-card-${acc.id}`}>
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={acc.name}>
                        {acc.name}
                      </Typography>
                      {/* 🔴 两种"缺失"是两件事（铁律 107）：卡不在结果里 vs 卡在但没账期 */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                        data-testid={`credit-nobill-${acc.id}`}
                      >
                        {bill === null
                          ? '账单数据暂不可用 —— 卡本身还在，编辑与明细不受影响'
                          : '未配置账期（在账户页编辑这张卡，填账单日与还款日）'}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                        {onNewTxn !== undefined && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              onNewTxn(acc);
                            }}
                            data-testid={`credit-txn-${acc.id}`}
                          >
                            记一笔
                          </Button>
                        )}
                        {onOpenLedger !== undefined && (
                          // 🔴 未配账期 ⇒ **不给「账单」入口**（那张卡没有账期可看，进了也是 400）
                          <Button
                            size="small"
                            onClick={() => onOpenLedger(acc)}
                            data-testid={`credit-ledger-${acc.id}`}
                          >
                            账户流水
                          </Button>
                        )}
                        {onEdit !== undefined && (
                          <Button size="small" onClick={() => onEdit(acc)} data-testid={`credit-edit-${acc.id}`}>
                            编辑
                          </Button>
                        )}
                        {onDelete !== undefined && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => onDelete(acc)}
                            data-testid={`credit-delete-${acc.id}`}
                          >
                            删除
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              }
              const item = bill;
              /**
               * 🔴 诚实分支已排掉 `bill === null` 与 `bill.current === null` ⇒ 这里必有账期。
               * 用显式断言而不是 `?.` / `?? 0`：**算不出来就让类型检查报错**，
               * 而不是在运行时悄悄退化成 0（那正是 K5 的成因）。
               */
              const current = item.current as NonNullable<CreditBillsView['current']>;
              const due = current.due;
              const days = current.daysUntilRepayment;
              return (
                <Card key={item.accountId} variant="outlined" data-testid={`credit-card-${item.accountId}`}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    {/* ── 卡名 + 档位 ── */}
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={item.name}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {item.tailNumber === null ? '未填尾号' : `尾号 ${item.tailNumber}`}
                          {item.creditLimit === null
                            ? ' · 额度未填'
                            : ` · 额度 ${formatMoneyText(toCents(item.creditLimit), item.currency)}`}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={creditStandingLabel(item.standing)}
                        sx={standingChipSx(item.standing, tk)}
                        data-testid={`credit-standing-${item.accountId}`}
                      />
                    </Stack>

                    {/* ── 本期应还 ── */}
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        本期应还
                      </Typography>
                      <Amount
                        cents={toCents(due)}
                        currency={item.currency}
                        colored={false}
                        sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}
                        data-testid={`credit-due-${item.accountId}`}
                      />
                    </Box>

                    {/* ── 🆕 已还 / 优惠 / 状态（批 `106-B` · `AC-B2.58` 四段）──
                        🔴 只在 `paid` 键存在时渲染（服务端只在传入还款腿时填 ——
                        不臆造"未还"）；优惠恒显示（0 也显示，让"四段"结构稳定）。 */}
                    {current.paid !== undefined && (
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                        data-testid={`credit-paid-${item.accountId}`}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            已还
                          </Typography>
                          <Amount
                            cents={toCents(current.paid.repaid)}
                            currency={item.currency}
                            colored={false}
                            sx={{ fontSize: '0.9375rem', fontWeight: 600 }}
                            data-testid={`credit-repaid-${item.accountId}`}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            剩余
                          </Typography>
                          <Amount
                            cents={toCents(current.paid.remaining)}
                            currency={item.currency}
                            colored={false}
                            sx={{ fontSize: '0.9375rem', fontWeight: 600 }}
                            data-testid={`credit-remaining-${item.accountId}`}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            优惠
                          </Typography>
                          <Amount
                            cents={toCents(current.waiverApplied ?? 0)}
                            currency={item.currency}
                            colored={false}
                            sx={{ fontSize: '0.9375rem', fontWeight: 600 }}
                            data-testid={`credit-waiver-${item.accountId}`}
                          />
                        </Box>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={paidStatusLabel(current.paid.status)}
                          sx={paidStatusChipSx(current.paid.status, tk)}
                          data-testid={`credit-paid-status-${item.accountId}`}
                        />
                      </Stack>
                    )}

                    {/* ── 账期 ──（🔴 走到这里必有账期：`current === null` 已在诚实分支返回） */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                      data-testid={`credit-period-${item.accountId}`}
                    >
                      {/*
                        🔴 `DR-080`：本期已还清时**不得**再显示「已过 N 天」。
                        修前这里只看 `days < 0` ⇒ 用户按时还了本期、只是之后又刷了卡，
                        账期行仍然写「已过 8 天」，与上面 chip 和账单页的「已结清」三处打架。
                      */}
                      {`账单日 ${current.billDay} · 还款日 ${current.repaymentDate}${
                        current.paid !== undefined && Number(current.paid.remaining) === 0
                          ? ' · 本期已还清'
                          : days >= 0
                            ? ` · 剩 ${String(days)} 天`
                            : ` · 已过 ${String(-days)} 天`
                      }`}
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    {/* ── 未出账单 + 剩余额度 ── */}
                    <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          未出账单
                        </Typography>
                        <Amount
                          cents={toCents(item.unbilled?.net ?? 0)}
                          currency={item.currency}
                          colored={false}
                          sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                          data-testid={`credit-unbilled-${item.accountId}`}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          剩余额度
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontFamily: (t) => t.jiaying.font.num }}
                          data-testid={`credit-available-${item.accountId}`}
                        >
                          {item.availableCredit === null
                            ? '—'
                            : formatMoneyText(toCents(item.availableCredit), item.currency)}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.usageE4 === null
                            ? '额度使用率（额度未填，无法计算）'
                            : `额度已用 ${String(divideExact(item.usageE4, 100))}%${
                                item.usageLevel === null ? '' : ` · ${creditUsageLabel(item.usageLevel)}`
                              }`}
                        </Typography>
                        {/* 🔴 算不出（额度未填 / 余额非负）⇒ **不显示进度条**，而不是画一条 0%（`AC-B2.8`） */}
                        {item.usageE4 !== null && (
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(divideExact(item.usageE4, 100), 100)}
                            sx={{
                              mt: 0.5,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: tk['c-surface-sunk'],
                              '& .MuiLinearProgress-bar': {
                                bgcolor: usageBarColor(item.usageLevel, tk),
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Stack>

                    {/* ── 🆕 分期未还（`DR-060` §4.3 · `AC-B2.24`）：第三块，与上面两块**并存不合并** ──
                        负债口径（Σ pending 期次本金，不含手续费），**不计入本期应还** —— 必须写明防误读 */}
                    {item.installment !== null && (
                      <Box
                        sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}
                        data-testid={`credit-installment-${item.accountId}`}
                      >
                        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            分期未还
                          </Typography>
                          <Amount
                            cents={toCents(item.installment.remainingPrincipal)}
                            currency={item.currency}
                            colored={false}
                            sx={{ fontSize: '0.9375rem', fontWeight: 700 }}
                            data-testid={`credit-installment-amount-${item.accountId}`}
                          />
                          <Typography variant="caption" color="text.secondary">
                            （剩 {String(item.installment.remainingPeriods)} 期
                            {item.installment.estimatedEndBill !== null
                              ? ` · 预计 ${item.installment.estimatedEndBill.slice(0, 7)} 结清`
                              : ''}
                            ）
                          </Typography>
                          <Chip size="small" label="不计入本期应还" variant="outlined" sx={{ height: 20 }} />
                        </Stack>
                      </Box>
                    )}

                    {/* ── 动作：账单动作 + 账户动作**合并在这一张卡上**（`DR-058`）── */}
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap', rowGap: 0.5 }}
                    >
                      {onNewTxn !== undefined && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            onNewTxn(acc);
                          }}
                          data-testid={`credit-txn-${acc.id}`}
                        >
                          记一笔
                        </Button>
                      )}
                      {/*
                        🆕 **账单（主入口）**（批 `106-A` · `AC-B2.27`）：
                        打开**账单独立页** —— 本期 / 未出 / 往期三档都能看明细，
                        不再是"整个账户流水"。用户的原话是「账单要能**单独**看到」。
                      */}
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          navigate(`/accounts/${encodeURIComponent(acc.id)}/bill`);
                        }}
                        data-testid={`credit-bill-${acc.id}`}
                      >
                        账单
                      </Button>
                      {onOpenLedger !== undefined && (
                        <Button
                          size="small"
                          onClick={() => {
                            onOpenLedger(acc);
                          }}
                          data-testid={`credit-ledger-${acc.id}`}
                        >
                          {/* 🔴 文案明确区分（`AC-B2.35`）：这个是**全量流水**（不按账期切），
                              「账单」才是按期的那一视图 —— 不改名用户会以为两个是一样的 */}
                          账户流水
                        </Button>
                      )}
                      {/* 🆕 分期入口（`DR-060` · 批 104）：跳分期页并锚定该卡 */}
                      <Button
                        size="small"
                        onClick={() => {
                          navigate(`/installments?accountId=${encodeURIComponent(acc.id)}`);
                        }}
                        data-testid={`credit-installments-${acc.id}`}
                      >
                        分期
                      </Button>
                      {onEdit !== undefined && (
                        <Button
                          size="small"
                          onClick={() => {
                            onEdit(acc);
                          }}
                          data-testid={`credit-edit-${acc.id}`}
                        >
                          编辑
                        </Button>
                      )}
                      {onDelete !== undefined && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            onDelete(acc);
                          }}
                          data-testid={`credit-delete-${acc.id}`}
                        >
                          删除
                        </Button>
                      )}
                      <Button
                        size="small"
                        onClick={() => {
                          setHistoryId(item.accountId);
                        }}
                        data-testid={`credit-history-${item.accountId}`}
                      >
                        往期
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={payersFor(item).length === 0}
                        onClick={() => {
                          setRepayId(item.accountId);
                        }}
                        data-testid={`credit-repay-${item.accountId}`}
                      >
                        还款
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Grid>
        </Box>
      )}

      {repayItem !== null && (
        <CreditRepayDialog
          item={repayItem}
          payers={payersFor(repayItem)}
          onClose={() => {
            setRepayId(null);
          }}
          onDone={() => {
            setRepayId(null);
          }}
        />
      )}

      {historyItem !== null && (
        <Dialog open onClose={() => setHistoryId(null)} fullWidth maxWidth="sm" data-testid="credit-history-dialog">
          <DialogTitle>往期账单 · {historyItem.name}</DialogTitle>
          <DialogContent>
            <PeriodTable item={historyItem} />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setHistoryId(null);
              }}
            >
              关闭
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

/**
 * 档位 chip 的配色。
 *
 * 🔴 `AC-B2.6`：**「应还」绝不用支出绿**（绿在财务界面里表示"钱变少 / 亏损"，
 * 用它标"欠款"会被读成"这是一笔支出"）。⇒ 中性 outlined；
 * 只有"临近"用琥珀（`c-notice`）、"已过"用朱红（`c-alert`）—— 两者是两个不同令牌（铁律 8）。
 */
function standingChipSx(
  standing: string,
  tk: Record<string, string | undefined>,
): Record<string, unknown> {
  if (standing === 'overdue') return { bgcolor: tk['c-alert-bg'], color: tk['c-alert'], borderColor: tk['c-alert'] };
  if (standing === 'dueSoon') return { bgcolor: tk['c-notice-bg'], color: tk['c-notice'], borderColor: tk['c-notice'] };
  return {};
}

/**
 * 🆕 还款状态文案（批 `106-B` · `AC-B2.59`）。
 *
 * 🔴 三态由服务端按数值推导（`creditPaidStatusOf` 唯一实现），端侧**只翻译不重算** ——
 * 端侧再写一份 if/else 就是第二个判据（铁律 24）。
 */
function paidStatusLabel(status: string): string {
  if (status === 'paid') return '已结清';
  if (status === 'partial') return '部分已还';
  return '未还';
}

/** 🆕 还款状态 chip 配色 —— 🔴 已结清 = 品牌色（不是绿：绿在财务界面是"钱变少"） */
function paidStatusChipSx(
  status: string,
  tk: Record<string, string | undefined>,
): Record<string, unknown> {
  if (status === 'paid') return { bgcolor: tk['c-brand-weak'], color: tk['c-brand'], borderColor: tk['c-brand'] };
  if (status === 'partial') return { bgcolor: tk['c-notice-bg'], color: tk['c-notice'], borderColor: tk['c-notice'] };
  return {};
}

/** 使用率进度条的颜色：常规 = 品牌色，偏紧 = 琥珀，告急 = 朱红 */
function usageBarColor(
  level: string | null,
  tk: Record<string, string | undefined>,
): string | undefined {
  if (level === 'over') return tk['c-alert'];
  if (level === 'notice') return tk['c-notice'];
  return tk['c-brand'];
}

/** 往期 + 未出的一张简表（每期一行：账单区间 / 消费 / 退款 / 应还 / 🆕 已还与状态） */
function PeriodTable({ item }: { item: CreditBillsView }): ReactElement {
  const rows: {
    key: string;
    label: string;
    consumed: number;
    refunded: number;
    due: number;
    /** 🆕 批 `106-B`：已还三件套（未出账单恒无 —— 它没有 `due` 可言） */
    paid?: { repaid: number; remaining: number; status: string };
    /** 🆕 批 `106-B`：该期优惠（0 / 无 ⇒ 不出键） */
    waiverApplied?: number;
    note?: string;
  }[] = [
    ...(item.unbilled === null
      ? []
      : [
          {
            key: 'unbilled',
            label: '未出账单',
            consumed: item.unbilled.consumed,
            refunded: item.unbilled.refunded,
            due: item.unbilled.net,
            note: `${item.unbilled.windowStart} ~ ${item.unbilled.windowEnd}`,
          },
        ]),
    ...(item.current === null
      ? []
      : [
          {
            key: 'current',
            label: `本期（${item.current.billDay}）`,
            consumed: item.current.consumed,
            refunded: item.current.refundedInWindow,
            due: item.current.due,
            ...(item.current.paid === undefined ? {} : { paid: item.current.paid }),
            ...(item.current.waiverApplied === undefined
              ? {}
              : { waiverApplied: item.current.waiverApplied as number }),
            note: `${item.current.windowStart} ~ ${item.current.windowEnd}`,
          },
        ]),
    ...item.history.map((h) => ({
      key: `h-${h.billDay}`,
      label: `${h.billDay} 账单`,
      consumed: h.consumed,
      refunded: h.refundedInWindow,
      due: h.due,
      // 🆕 批 `106-B`：往期也带已还三件套与优惠（服务端 `computeCreditBillHistory` 统一填）
      ...(h.paid === undefined ? {} : { paid: h.paid }),
      ...(h.waiverApplied === undefined ? {} : { waiverApplied: h.waiverApplied as number }),
      note: `${h.windowStart} ~ ${h.windowEnd}`,
    })),
  ];

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        这张卡还没配账期，也没有可回溯的账期窗口。
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25} data-testid="credit-period-table">
      {rows.map((r) => (
        <Box key={r.key}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {r.label}
              </Typography>
              {r.note !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  {r.note}
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Amount
                cents={toCents(r.due)}
                currency={item.currency}
                colored={false}
                sx={{ fontSize: '0.9375rem', fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                消费 {formatMoneyText(toCents(r.consumed), item.currency)}
                {r.refunded !== 0 && ` · 退款 ${formatMoneyText(toCents(r.refunded), item.currency)}`}
                {/* 🆕 优惠与已还（批 `106-B`）—— 0 / 无不出（可选键剔键纪律的镜像展示） */}
                {(r.waiverApplied ?? 0) > 0 &&
                  ` · 优惠 ${formatMoneyText(toCents(r.waiverApplied as number), item.currency)}`}
                {r.paid !== undefined &&
                  ` · 已还 ${formatMoneyText(toCents(r.paid.repaid), item.currency)}（${paidStatusLabel(r.paid.status)}）`}
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ mt: 1 }} />
        </Box>
      ))}
      <Typography variant="caption" color="text.secondary">
        口径：窗口按「上期账单日（含）→ 本期账单日（不含）」切分，账单日当天的消费算下一期；
        未出账单从本期账单日到今天。与银行账单对不上时以银行为准，并检查账期设置。
        {/* 🆕 批 `106-B`：仅回溯最近 6 期（`AC-B2.63`）—— 更早的期次不编造金额 */}
        仅回溯最近 {HISTORY_MONTHS} 期，更早的期次请以银行账单为准。
      </Typography>
    </Stack>
  );
}
